import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { Pressable, StyleSheet, View, type ViewProps } from 'react-native'
import { HelperText, Text, useTheme } from 'react-native-paper'

export type SelectOption = {
    label: string
    value: string
}

export type SelectInputProps<T extends FieldValues = Record<string, unknown>> = {
    name: Path<T>
    control: Control<T>
    label: string
    options: SelectOption[]
    hint?: string
    horizontal?: boolean
    wrapperProps?: ViewProps
}

export function SelectInput<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    label,
    options,
    hint,
    horizontal = false,
    wrapperProps = {},
}: SelectInputProps<T>) {
    const theme = useTheme()
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    return (
        <View style={[styles.inputWrapper, wrapperProps.style]} {...wrapperProps}>
            {label ? (
                <Text variant="bodyMedium" style={styles.selectLabel}>
                    {label}
                </Text>
            ) : null}
            <View style={[styles.radioContainer, horizontal && styles.radioContainerHorizontal]}>
                {options.map(option => {
                    const isSelected = field.value === option.value
                    return (
                        <Pressable
                            key={option.value}
                            onPress={() => field.onChange(option.value)}
                            style={[
                                styles.radioOption,
                                horizontal && styles.radioOptionHorizontal,
                                isSelected && {
                                    backgroundColor: theme.colors.primaryContainer,
                                },
                            ]}
                        >
                            <Text variant="bodyMedium" style={styles.radioTitle}>
                                {option.label}
                            </Text>
                        </Pressable>
                    )
                })}
            </View>
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 0,
    },
    selectLabel: {
        marginBottom: 8,
        fontWeight: '600',
    },
    radioContainer: {
        justifyContent: 'flex-start',
    },
    radioContainerHorizontal: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        gap: 4,
    },
    radioOption: {
        alignItems: 'center',
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    radioOptionHorizontal: {
        flexDirection: 'column',
    },
    radioTitle: {
        fontWeight: '500',
    },
})
